import { skipToken, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useGeosets, getGeosetFence, geosetFenceDetailKey } from '@/services/db/geoset/queries';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { isRecord } from '@/utils/objects';
import { LOG_TYPE } from './activity-log-types';
import type { GeoRefResolver } from './components/log-json-formatter';
import type { ActivityLog } from './types';

/**
 * Resolves the id attributes on a geofence transition event to names for the expanded-row
 * JSON view: `geoset_id` → the geoset's name, linked to its page; `geofence_id` → the fence's
 * name, linked to its page in that geoset. An id whose entity is gone (deleted geoset/fence)
 * resolves to `null`, so the raw id renders unannotated.
 *
 * The lookups (workspace geoset list + a by-id read of the one fence) only fetch for geofence
 * logs, and only once the row is expanded (this hook mounts with the expanded row).
 */

const FENCE_STALE_TIME = 60_000;

/** Coerce a wire id (number or numeric string) to the data layer's string id shape. */
function toIdString(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return value.trim();
  return null;
}

export function useGeoRefResolver(log: ActivityLog, workspaceId: string): GeoRefResolver | undefined {
  const router = useRouter();
  const isGeofenceLog = log.type === LOG_TYPE.Geofence;
  const attrs = isRecord(log.attrs) ? log.attrs : null;
  const geosetId = isGeofenceLog ? toIdString(attrs?.geoset_id) : null;
  const fenceId = isGeofenceLog ? toIdString(attrs?.geofence_id) : null;

  const geosetsQuery = useGeosets({ queryConfig: { enabled: isGeofenceLog } });

  // By-id read of the one referenced fence (not the geoset's full membership); shares the
  // fence-detail cache with the geoset pages. A 404 (deleted fence) leaves `data` unset.
  // The numeric workspace scope comes from the URL — the same source `useGeosets` derives its
  // scope from — so both lookups always target the same workspace, and both stay disabled on
  // non-numeric routes (`/workspaces/last/…`, where the activity-log APIs resolve `last`
  // server-side but the geoset data layer has no workspace to scope to).
  const workspaceIdNumber = useWorkspaceIdFromCurrentUrl();
  const canFetchFence = geosetId != null && fenceId != null && workspaceIdNumber != null && workspaceIdNumber > 0;
  const fenceQuery = useQuery({
    queryKey: canFetchFence
      ? geosetFenceDetailKey(workspaceIdNumber, geosetId, fenceId)
      : ['geoset', 'fence-detail', 'no-ref'],
    queryFn: canFetchFence ? () => getGeosetFence(workspaceIdNumber, geosetId, fenceId) : skipToken,
    staleTime: FENCE_STALE_TIME,
  });

  if (!isGeofenceLog) return undefined;

  return (key, value) => {
    const id = toIdString(value);
    if (id == null) return null;

    if (key === 'geoset_id') {
      const geoset = geosetsQuery.data?.geosets.find((candidate) => candidate.id === id);
      if (geoset == null) return null;
      const href = router.buildLocation({
        to: '/workspaces/$workspaceId/geosets/$geosetId',
        params: { workspaceId, geosetId: geoset.id },
      }).href;
      return { name: geoset.name, href };
    }

    if (key === 'geofence_id' && geosetId != null) {
      const fence = fenceQuery.data;
      if (fence == null || fence.id !== id) return null;
      const href = router.buildLocation({
        to: '/workspaces/$workspaceId/geosets/$geosetId/geofences/$geofenceId',
        params: { workspaceId, geosetId, geofenceId: fence.id },
      }).href;
      return { name: fence.name, href };
    }

    return null;
  };
}
